import React, { useRef, useMemo, useState } from 'react';
import { Row, Col, Button, Spinner, Form, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSync, faSearch } from '@fortawesome/free-solid-svg-icons';
import ReportRuleCard from './ReportRuleCard';
import ReportRuleForm from './ReportRuleForm';
import DeleteReportRuleModal from './DeleteReportRuleModal';
import ReportRuleFilterBar from './ReportRuleFilterBar';
import { useReportRules } from '../hooks/useReportRules';
import '../styles/ReportRules.css'; // reuse existing styles


const ReportRulesList = ({ meters, sources, currentFinancialYear }) => {
  const { data: reportRules = [], isLoading, error, refetch } = useReportRules();
  const reportRuleFormRef = useRef(null);
  const deleteModalRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    sourceIds: [],
    subLocationIds: [],
    modules: [],
    categories: [],
    subCategories: [],
    kpis: [],
    meterIds: [],
    enabledStatus: null, // null = all, true = enabled, false = disabled
  });

  // ── Filter logic ────────────────────────────────────────────────────────

  const filteredReportRules = useMemo(() => {
    let result = reportRules;

    if (searchTerm) {
      result = result.filter((rule) =>
        rule.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    if (filters.sourceIds.length > 0) {
      result = result.filter((rule) =>
        rule.criteria?.sourceId?.some((id) => filters.sourceIds.includes(id)),
      );
    }

    if (filters.modules.length > 0) {
      result = result.filter((rule) => filters.modules.includes(rule.criteria?.module));
    }

    if (filters.categories.length > 0) {
      result = result.filter((rule) =>
        rule.criteria?.category?.some((cat) => filters.categories.includes(cat)),
      );
    }

    if (filters.subCategories.length > 0) {
      result = result.filter((rule) =>
        rule.criteria?.subCategory?.some((sc) => filters.subCategories.includes(sc)),
      );
    }

    if (filters.kpis.length > 0) {
      result = result.filter((rule) =>
        rule.criteria?.kpi?.some((kpi) => filters.kpis.includes(kpi)),
      );
    }

    if (filters.meterIds.length > 0) {
      result = result.filter((rule) =>
        rule.criteria?.meterId?.some((id) => filters.meterIds.includes(id)),
      );
    }

    if (filters.enabledStatus !== null) {
      result = result.filter((rule) => rule.isEnabled === filters.enabledStatus);
    }

    return result;
  }, [reportRules, searchTerm, filters]);

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleAddReportRule = () => reportRuleFormRef.current?.show();
  const handleEditReportRule = (rule) => reportRuleFormRef.current?.show(rule);
  const handleDeleteReportRule = (rule) => deleteModalRef.current?.show(rule);

  const handleClearFilters = () => {
    setFilters({
      sourceIds: [],
      subLocationIds: [],
      modules: [],
      categories: [],
      subCategories: [],
      kpis: [],
      meterIds: [],
      enabledStatus: null,
    });
    setSearchTerm('');
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="alert-rules-container">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Report Rules</h2>
          <p className="text-muted mb-0">Configure scheduled reports for your IoT meters</p>
        </div>
        <Button variant="primary" onClick={handleAddReportRule}>
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Add Report Rule
        </Button>
      </div>

      {/* Search */}
      <div className="mb-3">
        <InputGroup>
          <InputGroup.Text>
            <FontAwesomeIcon icon={faSearch} />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search report rules by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>
      </div>

      {/* Filters */}
      <ReportRuleFilterBar
        meters={meters}
        filters={filters}
        onFiltersChange={setFilters}
        onClearFilters={handleClearFilters}
        sources={sources}
        currentFinancialYear={currentFinancialYear}
      />

      {/* Error */}
      {error && (
        <div className="alert alert-danger" role="alert">
          Failed to load report rules. Please try again.
          <Button
            variant="outline-danger"
            size="sm"
            className="ms-3"
            onClick={() => refetch()}
          >
            <FontAwesomeIcon icon={faSync} className="me-1" /> Retry
          </Button>
        </div>
      )}

      {/* List */}
      <div className="alert-rules-list">
        {isLoading ? (
          <div className="text-center p-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading report rules...</p>
          </div>
        ) : filteredReportRules.length > 0 ? (
          <>
            <div className="mb-3 text-muted">
              Showing {filteredReportRules.length} of {reportRules.length} report rules
            </div>
            <Row>
              {filteredReportRules.map((rule) => (
                <Col md={6} lg={4} className="mb-4" key={rule.id}>
                  <ReportRuleCard
                    rule={rule}
                    onEdit={handleEditReportRule}
                    onDelete={handleDeleteReportRule}
                  />
                </Col>
              ))}
            </Row>
          </>
        ) : (
          <div className="text-center p-5 empty-state">
            <h4>No report rules found</h4>
            <p>
              {reportRules.length === 0
                ? 'Get started by creating your first report rule.'
                : 'Try adjusting your search or filters.'}
            </p>
            {reportRules.length === 0 ? (
              <Button variant="primary" onClick={handleAddReportRule} className="mt-2">
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Create Your First Report Rule
              </Button>
            ) : (
              <Button variant="outline-primary" onClick={handleClearFilters} className="mt-2">
                <FontAwesomeIcon icon={faSync} className="me-2" />
                Clear Filters
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <ReportRuleForm
        ref={reportRuleFormRef}
        onSave={() => refetch()}
        meters={meters}
        sources={sources}
        currentFinancialYear={currentFinancialYear}
      />

      <DeleteReportRuleModal
        ref={deleteModalRef}
        onDelete={() => refetch()}
      />
    </div>
  );
};

export default ReportRulesList;